import { Control, FieldValues, Path } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import { RolesEnum } from "@/constants/enums/roles.enum";
import { useClientRoleOptions } from "@/hooks/useEnumOptions";

type RoleOption = { value: RolesEnum; label: string };

type UserFormProps<T extends FieldValues> = {
  control: Control<T>;
  rolesOptions?: RoleOption[];
  showPassword?: boolean;
  showRole?: boolean;
};

export function UserForm<T extends FieldValues>({
  control,
  rolesOptions,
  showPassword = false,
  showRole = true,
}: UserFormProps<T>) {
  const { t } = useTranslation("shared");
  const clientRoleOptions = useClientRoleOptions();
  return (
    <>
      {showRole && (
        <ControlledSelectV2
          control={control}
          name={"role" as Path<T>}
          label={t("userForm.roleLabel")}
          options={rolesOptions ?? clientRoleOptions}
        />
      )}
      <ControlledUnifiedInput
        control={control}
        name={"lastName" as Path<T>}
        label={t("userForm.lastNameLabel")}
      />
      <ControlledUnifiedInput
        control={control}
        name={"firstName" as Path<T>}
        label={t("userForm.firstNameLabel")}
      />
      <ControlledUnifiedInput
        control={control}
        name={"email" as Path<T>}
        label={t("userForm.emailLabel")}
      />
      <StyledPhoneInput
        control={control}
        name={"mobilePhoneNumber" as Path<T>}
        title={t("userForm.mobilePhoneLabel")}
        defaultCountry="FR"
      />
      <StyledPhoneInput
        control={control}
        name={"fixedPhoneNumber" as Path<T>}
        title={t("userForm.fixedPhoneLabel")}
        placeholder={t("userForm.fixedPhoneLabel")}
        defaultCountry="FR"
      />
      {showPassword && (
        <>
          <ControlledUnifiedInput
            control={control}
            name={"password" as Path<T>}
            label={t("userForm.passwordLabel")}
            inputType="password"
          />
          <ControlledUnifiedInput
            control={control}
            name={"confirmPassword" as Path<T>}
            label={t("userForm.confirmPasswordLabel")}
            inputType="password"
          />
        </>
      )}
    </>
  );
}
